// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./HyperPayCopyAndPayWidget.css";
import madaLogoUrl from "assets/mada.svg";
import visaLogoUrl from "assets/visa-placeholder.svg";
import mastercardLogoUrl from "assets/mastercard-placeholder.svg";
import {
  canUseApplePayInBrowser,
  sanitizeHyperPayWpwlOptions,
  stripApplePayFromBrands,
} from "utils/applePay.util";
import {
  readSelectedCardBrand,
  trackPaymentStarted,
} from "services/mixpanel.service";

const BRAND_DISPLAY: Record<string, string> = {
  APPLEPAY: "Apple Pay",
  MADA: "mada",
  VISA: "Visa",
  MASTER: "Mastercard",
};

const BRAND_LOGOS: Record<string, string> = {
  // APPLEPAY intentionally omitted — the black pill + "Apple Pay" text is cleaner
  // than rendering the PNG at ~22px where it risks showing as a broken-image circle.
  MADA: madaLogoUrl,
  VISA: visaLogoUrl,
  MASTER: mastercardLogoUrl,
};

/**
 * HyperPay COPYandPAY (OPPWA paymentWidgets.js)
 *
 * Card vs Apple Pay often use different entityIds in production → two checkout sessions (two checkoutIds).
 * Each script URL is .../paymentWidgets.js?checkoutId=<one id>. Loading two widgets at once shares one global
 * `window.wpwl` and breaks Safari; we unload before switching (see unloadHyperPayWidgetScript). Parent UI
 * renders only the active tab's widget (CustomPayment hyperPayTab).
 */

/**
 * Multi-brand widget: ensure scheme logos (.wpwl-brand) are siblings of .wpwl-wrapper-brand
 * so CSS can place logos beside the dropdown. Some OPPWA builds nest .wpwl-brand inside the wrapper.
 */
function hoistBrandIconsBesideDropdown() {
  document.querySelectorAll(".hyperpay-widget-wrapper:not(.hyperpay-widget--single-brand)").forEach((root) => {
    root.querySelectorAll(".wpwl-group-brand").forEach((group) => {
      const wrap = group.querySelector(".wpwl-wrapper-brand");
      if (!wrap) return;
      const nested = wrap.querySelector(".wpwl-brand");
      if (!nested) return;
      if (nested.parentElement !== group) {
        group.insertBefore(nested, wrap.nextSibling);
      }
    });
  });
}

/** Move custom save-card UI directly above OPPWA Pay button. */
function mountSaveCardSlotAbovePayButton(root: Element, slotHost: HTMLElement) {
  if (!root || !slotHost) return false;
  const submitGroup =
    root.querySelector(".wpwl-group-submit") ||
    root.querySelector(".wpwl-button-pay")?.closest(".wpwl-group");
  if (!submitGroup?.parentElement) return false;

  let wrapper = root.querySelector(".hyperpay-save-card-slot") as HTMLElement | null;
  if (!wrapper) {
    wrapper = document.createElement("div");
    wrapper.className = "hyperpay-save-card-slot";
  }
  if (slotHost.parentElement !== wrapper) {
    wrapper.appendChild(slotHost);
  }
  if (submitGroup.previousElementSibling !== wrapper) {
    submitGroup.parentElement.insertBefore(wrapper, submitGroup);
  }
  return true;
}

/**
 * OPPWA only returns a registration token when the shopper opts in via
 * checkbox[name="createRegistration"]. Our custom checkbox must mirror that
 * hidden/native field right before Pay is submitted.
 */
function syncHyperPayCreateRegistrationOptIn(formOrRoot: Element | null | undefined, enabled: boolean) {
  const forms = new Set<Element>();
  if (formOrRoot instanceof HTMLFormElement) {
    forms.add(formOrRoot);
  } else if (formOrRoot?.querySelectorAll) {
    formOrRoot
      .querySelectorAll("form.wpwl-form-card, form.paymentWidgets, form[class*='wpwl-form']")
      .forEach((f) => forms.add(f));
  }
  if (typeof document !== "undefined") {
    document
      .querySelectorAll(
        ".hyperpay-widget-wrapper form.wpwl-form-card, .hyperpay-widget-wrapper form.paymentWidgets"
      )
      .forEach((f) => forms.add(f));
  }

  forms.forEach((form) => {
    let input = form.querySelector<HTMLInputElement>('input[name="createRegistration"]');
    if (!input && enabled) {
      input = document.createElement("input");
      input.type = "checkbox";
      input.name = "createRegistration";
      input.style.display = "none";
      form.appendChild(input);
    }
    if (!input) return;
    input.checked = enabled;
    input.value = enabled ? "true" : "false";
    input.disabled = false;
  });
}

/** OPPWA injects visible labels even when inputs have placeholders — hide them. */
function hideHyperPayFieldLabels() {
  document
    .querySelectorAll(
      ".hyperpay-widget-wrapper .wpwl-label, .hyperpay-widget-wrapper [class*='wpwl-label']"
    )
    .forEach((el) => {
      (el as HTMLElement).style.setProperty("display", "none", "important");
    });
}

const HIDDEN_WPWL_INPUT_NAMES = [
  "generalTermsAndConditions",
  "createRegistration",
  "schemeOptIn",
  "ncpOptIn",
  "omniTokenOptIn",
  "installmentPlan",
  "installmentPlanId",
  "numberOfInstallments",
  "birthDate",
  "dateOfBirth",
  "date_of_birth",
  "customer.birthDate",
  "customer.dateOfBirth",
  "card.birthDate",
  "card.dateOfBirth",
  "cardholderBirth",
  "country",
  "countryCode",
  "billing.country",
  "customer.country",
  "card.country",
  "phone",
  "mobile",
  "customer.phone",
  "customer.mobile",
  "billing.phone",
];

const HIDDEN_WPWL_GROUP_CLASS_PATTERNS = [
  "installment",
  "country",
  "phone",
  "mobile",
  "dateofbirth",
  "birthdate",
  "dob",
  "registration",
  "generalterms",
  "scheme",
  "ncp",
  "clicktopay",
];

function shouldHideWpwlGroupByText(text: string) {
  const t = text.toLowerCase();
  return (
    t.includes("click to pay") ||
    t.includes("share my card details") ||
    t.includes("billing address and email") ||
    t.includes("installment plan") ||
    (t.includes("terms and conditions") && t.includes("installment")) ||
    (t.includes("enroll") && t.includes("scheme")) ||
    t.includes("date of birth") ||
    t.includes("birth date") ||
    t.includes("birthdate") ||
    (t.includes("country") && (t.includes("code") || t.includes("select"))) ||
    t.includes("mobile number") ||
    (t.includes("phone") && !t.includes("iphone"))
  );
}

function shouldHideWpwlGroupByClass(className: string, registrationCheckout = false) {
  const c = className.toLowerCase();
  return HIDDEN_WPWL_GROUP_CLASS_PATTERNS.some((p) => {
    if (registrationCheckout && p === "registration") return false;
    return c.includes(p);
  });
}

/** Hide scheme / C2P consent, installments, phone, country, DOB — fields we never collect in checkout. */
function hideUnwantedWpwlGroups(options: { registrationCheckout?: boolean } = {}) {
  const registrationCheckout = !!options.registrationCheckout;
  const hiddenInputNames = registrationCheckout
    ? HIDDEN_WPWL_INPUT_NAMES.filter((name) => name !== "createRegistration")
    : HIDDEN_WPWL_INPUT_NAMES;

  document.querySelectorAll(".hyperpay-widget-wrapper").forEach((root) => {
    const hideGroupOf = (el: Element | null | undefined) => {
      if (!el) return;
      const g = el.closest?.(".wpwl-group");
      if (g) (g as HTMLElement).style.setProperty("display", "none", "important");
    };

    hiddenInputNames.forEach((name) => {
      root
        .querySelectorAll(`input[name="${name}"], select[name="${name}"]`)
        .forEach((el) => hideGroupOf(el));
    });

    root.querySelectorAll(".wpwl-group").forEach((g) => {
      if (shouldHideWpwlGroupByClass(g.className || "", registrationCheckout)) {
        (g as HTMLElement).style.setProperty("display", "none", "important");
        return;
      }
      if (shouldHideWpwlGroupByText(g.textContent || "")) {
        (g as HTMLElement).style.setProperty("display", "none", "important");
      }
    });
  });
}

/**
 * Unified card form: hide brand tabs and pick the broadest OPPWA brand option
 * so Visa, Mastercard, and Mada are accepted from one set of fields.
 */
function collapseBrandSelectorForUnifiedForm(root: Element) {
  root.querySelectorAll(".wpwl-group-brand").forEach((group) => {
    group.querySelectorAll(".hyperpay-brand-pills").forEach((pills) => pills.remove());

    const select = group.querySelector<HTMLSelectElement>(
      "select.wpwl-control, select[name='paymentBrand']"
    );
    if (select) {
      const options = Array.from(select.options).filter((o) => !!o.value);
      const unified =
        options.find((o) => o.value.split(/\s+/).filter(Boolean).length >= 2) ||
        options[0];
      if (unified && select.value !== unified.value) {
        select.value = unified.value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
      (select as HTMLElement).style.cssText =
        "position:absolute!important;opacity:0!important;pointer-events:none!important;width:1px!important;height:1px!important;overflow:hidden!important;";
    }

    (group as HTMLElement).style.setProperty("display", "none", "important");
  });
}

function replaceBrandDropdownWithPills(root: Element) {
  root.querySelectorAll(".wpwl-group-brand").forEach((group) => {
    if (group.querySelector(".hyperpay-brand-pills")) return;

    const select = group.querySelector<HTMLSelectElement>(
      "select.wpwl-control, select[name='paymentBrand']"
    );
    if (!select || select.options.length === 0) return;

    (select as HTMLElement).style.cssText =
      "position:absolute!important;opacity:0!important;pointer-events:none!important;width:1px!important;height:1px!important;overflow:hidden!important;";

    const options = Array.from(select.options).filter((o) => !!o.value);
    const appleOptions = canUseApplePayInBrowser()
      ? options.filter((o) => o.value === "APPLEPAY")
      : [];

    const rawCardOptions = options.filter((o) => o.value !== "APPLEPAY");
    const coveredByCombo = new Set<string>();
    rawCardOptions.forEach((o) => {
      const tokens = o.value.split(" ").filter(Boolean);
      if (tokens.length > 1) tokens.forEach((t) => coveredByCombo.add(t));
    });
    const cardOptions = rawCardOptions.filter((o) => {
      const tokens = o.value.split(" ").filter(Boolean);
      return tokens.length > 1 || !coveredByCombo.has(o.value);
    });

    const pills = document.createElement("div");
    pills.className = "hyperpay-brand-pills";

    const setActive = (value: string) => {
      pills.querySelectorAll<HTMLButtonElement>(".hyperpay-brand-pill").forEach((p) => {
        p.classList.toggle("active", p.dataset.brand === value);
      });
    };

    const makePill = (option: HTMLOptionElement) => {
      const pill = document.createElement("button");
      pill.type = "button";
      pill.className = "hyperpay-brand-pill";
      if (option.value === "APPLEPAY") pill.classList.add("hyperpay-brand-pill--applepay");
      pill.dataset.brand = option.value;

      const tokens = option.value.split(" ").filter(Boolean);
      const logoTokens = tokens.filter((t) => BRAND_LOGOS[t]);
      if (logoTokens.length > 0) {
        logoTokens.forEach((t) => {
          const img = document.createElement("img");
          img.src = BRAND_LOGOS[t];
          img.alt = BRAND_DISPLAY[t] ?? t;
          img.className = "hyperpay-brand-pill__logo";
          pill.appendChild(img);
        });
      } else {
        pill.textContent = BRAND_DISPLAY[option.value] ?? option.text ?? option.value;
      }

      pill.addEventListener("click", () => {
        if (select.value !== option.value) {
          select.value = option.value;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        }
        setActive(option.value);
      });
      return pill;
    };

    appleOptions.forEach((o) => pills.appendChild(makePill(o)));

    if (appleOptions.length > 0 && cardOptions.length > 0) {
      const sep = document.createElement("span");
      sep.className = "hyperpay-brand-sep";
      sep.textContent = "or pay with card";
      pills.appendChild(sep);
    }

    cardOptions.forEach((o) => pills.appendChild(makePill(o)));

    group.appendChild(pills);

    const defaultOption = appleOptions[0] ?? cardOptions[0];
    if (defaultOption) {
      if (select.value !== defaultOption.value) {
        select.value = defaultOption.value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
      setActive(defaultOption.value);
    }
  });
}

/** Find the Apple Pay container inside a root element regardless of OPPWA version. */
function findApplePayGroup(root: Element): Element | null {
  const toDirectChild = (el: Element): Element | null => {
    let cur: Element | null = el;
    while (cur && cur.parentElement && cur.parentElement !== root) {
      cur = cur.parentElement;
    }
    return cur && cur.parentElement === root ? cur : null;
  };

  for (const sel of [
    '[class*="virtualAccount-APPLEPAY"]',
    '[id*="virtualAccount-APPLEPAY"]',
    '[id*="virtualAccount"]',
    ".wpwl-container-applepay",
    ".wpwl-form-applepay",
    "form.wpwl-form-applepay",
    '[class*="APPLEPAY"]',
    '[data-action*="APPLEPAY"]',
  ]) {
    const el = root.querySelector(sel);
    if (el) return toDirectChild(el) || el;
  }

  const btn: Element | undefined =
    root.querySelector("apple-pay-button") ||
    root.querySelector(".wpwl-apple-pay-button") ||
    Array.from(root.querySelectorAll("button")).find((b) =>
      /apple\s*pay/i.test((b as HTMLElement).textContent || "")
    );

  if (btn) return toDirectChild(btn) || btn;

  return null;
}

/** Enforce DOM order: Apple Pay → brand pills → card fields. */
function moveApplePaySectionToTop() {
  document.querySelectorAll(".hyperpay-widget-wrapper").forEach((root) => {
    const appleGroup = findApplePayGroup(root);
    if (!appleGroup) return;

    if (appleGroup !== root.firstElementChild) {
      root.insertBefore(appleGroup, root.firstChild);
    }
  });
}

/** Remove OPPWA inner card chrome when the modal is already the card shell. */
function flattenInModalCardChrome(root: Element | null) {
  if (!root || !root.classList.contains("hyperpay-widget--in-modal")) return;
  const selectors = [
    ".wpwl-container",
    ".wpwl-container-card",
    "form.wpwl-form",
    "form.wpwl-form-card",
    "form.paymentWidgets",
    '[class*="wpwl-form"]',
  ];
  selectors.forEach((sel) => {
    root.querySelectorAll(sel).forEach((el) => {
      const node = el as HTMLElement;
      node.style.padding = "0";
      node.style.margin = "0";
      node.style.background = "transparent";
      node.style.backgroundColor = "transparent";
      node.style.border = "none";
      node.style.boxShadow = "none";
      node.style.borderRadius = "0";
    });
  });
}

/** Beat OPPWA's late-loaded @media (min-width: 480px) 66% column grid in modals/narrow layouts. */
function forceFullWidthWpwlFieldGroups(root: Element | null) {
  if (!root) return;
  const groupSelectors = [
    ".wpwl-group-cardNumber",
    ".wpwl-group-cardHolder",
    ".wpwl-group-birthDate",
    ".wpwl-group-expiry",
    ".wpwl-group-cvv",
    ".wpwl-group-expiryMonth",
    ".wpwl-group-expiryYear",
    '[class*="wpwl-group-expiry"]',
    '[class*="wpwl-group-cvv"]',
    ".wpwl-wrapper-cardNumber",
    ".wpwl-wrapper-cardHolder",
    ".wpwl-wrapper-expiry",
    ".wpwl-wrapper-cvv",
  ];
  groupSelectors.forEach((sel) => {
    root.querySelectorAll(sel).forEach((el) => {
      const node = el as HTMLElement;
      node.style.setProperty("width", "100%", "important");
      node.style.setProperty("max-width", "100%", "important");
      node.style.setProperty("padding-right", "0", "important");
      node.style.setProperty("float", "none", "important");
      node.style.setProperty("flex", "1 1 100%", "important");
    });
  });
}

/** OPPWA global: unload before loading another checkout on the same page (required for Safari; Chrome is more forgiving). */
export function unloadHyperPayWidgetScript(scriptId: string) {
  try {
    if (typeof window !== "undefined" && window.wpwl && typeof window.wpwl.unload === "function") {
      window.wpwl.unload();
    }
  } catch {
    /* ignore */
  }
  const el = typeof document !== "undefined" && scriptId ? document.getElementById(scriptId) : null;
  if (el) el.remove();
}

function applyBrandUi(root: Element | Document, hideBrandSelector: boolean) {
  const scope = root === document ? document : root;
  if (hideBrandSelector) {
    collapseBrandSelectorForUnifiedForm(scope as Element);
    return;
  }
  hoistBrandIconsBesideDropdown();
  replaceBrandDropdownWithPills(scope as Element);
}

/** Card number and CVV render inside OPPWA iframes, which cannot see the page font or dir. */
function arabicIframeField(existing) {
  return {
    ...(existing || {}),
    "text-align": "right",
    "font-family": "Tahoma, Geeza Pro, Arial, sans-serif",
  };
}

/** Set window.wpwlOptions before paymentWidgets.js runs; chain server-provided onReady if present. */
function applyWpwlOptionsWithSanitizer(
  userWpwl,
  getSaveCardForSubmitRef,
  registrationCheckout = false,
  inModal = false,
  hideBrandSelector = false,
  lang = "en",
  onWidgetReady?: () => void
) {
  const isArabic = lang === "ar";
  const user = userWpwl && typeof userWpwl === "object" ? { ...userWpwl } : {};
  const userOnReady = user.onReady;
  const userOnBeforeSubmitCard = user.onBeforeSubmitCard;
  delete user.onReady;
  delete user.onBeforeSubmitCard;

  const runAfterWidgetReady = () => {
    hideUnwantedWpwlGroups({ registrationCheckout });
    hideHyperPayFieldLabels();
    if (canUseApplePayInBrowser()) {
      moveApplePaySectionToTop();
    }
    applyBrandUi(document, hideBrandSelector);
    if (inModal) {
      flattenInModalCardChrome(document.querySelector(".hyperpay-widget--in-modal"));
    }
    document.querySelectorAll(".hyperpay-widget-wrapper").forEach((wrapper) => {
      forceFullWidthWpwlFieldGroups(wrapper);
    });
  };

  const safeWpwl = sanitizeHyperPayWpwlOptions(user) || {};
  const existingIframeStyles = safeWpwl.iframeStyles || {};
  const iframeStyles = isArabic
    ? {
        ...existingIframeStyles,
        "card-number": arabicIframeField(existingIframeStyles["card-number"]),
        "card-number-placeholder": arabicIframeField(
          existingIframeStyles["card-number-placeholder"]
        ),
        cvv: arabicIframeField(existingIframeStyles.cvv),
        "cvv-placeholder": arabicIframeField(existingIframeStyles["cvv-placeholder"]),
      }
    : existingIframeStyles;

  window.wpwlOptions = {
    ...safeWpwl,
    /* Placeholders, the pay button, and validation copy follow this.
       The checkout request itself stays English; only the widget language changes. */
    locale: isArabic ? "ar" : "en",
    ...(isArabic ? { iframeStyles } : {}),
    showLabels: false,
    onReady: function (...args) {
      if (typeof userOnReady === "function") {
        userOnReady.apply(this, args);
      }
      runAfterWidgetReady();
      [0, 50, 150, 400, 800, 1500, 2500].forEach((ms) =>
        setTimeout(runAfterWidgetReady, ms)
      );
      /* Reveal after the first styling passes (brand pills, hidden groups)
         have run, so the form appears finished rather than restyling in
         front of the rider. */
      if (typeof onWidgetReady === "function") setTimeout(onWidgetReady, 180);
    },
    // Sync only — never block or delay payment submission.
    onBeforeSubmitCard: function (...args) {
      try {
        trackPaymentStarted(readSelectedCardBrand());
      } catch {
        /* analytics must never block payment */
      }
      try {
        const getSave =
          typeof getSaveCardForSubmitRef === "function"
            ? getSaveCardForSubmitRef
            : getSaveCardForSubmitRef?.current;
        const saveEnabled = !!getSave?.();
        syncHyperPayCreateRegistrationOptIn(this, saveEnabled);
      } catch {
        /* never block payment */
      }
      if (typeof userOnBeforeSubmitCard === "function") {
        return userOnBeforeSubmitCard.apply(this, args);
      }
      return true;
    },
  };
}

export interface HyperPayWidgetConfig {
  checkoutId: string;
  widgetBaseUrl: string;
  return_url: string;
  integrity?: string | null;
  wpwlOptions?: Record<string, unknown>;
  /** Wallet add-card: OPPWA registration widget (no payment). */
  registrationCheckout?: boolean;
}

interface HyperPayCopyAndPayWidgetProps {
  config: HyperPayWidgetConfig;
  scriptId?: string;
  dataBrands?: string;
  variant?: "card" | "applepay";
  /** Rendered directly above the OPPWA Pay button once the widget is ready. */
  abovePayButtonSlot?: React.ReactNode;
  /** Whether the shopper opted to save the card (drives OPPWA createRegistration on Pay). */
  getSaveCardForSubmit?: () => boolean;
  /** Flatten inner OPPWA card chrome when rendered inside AppModal. */
  inModal?: boolean;
  /** One unified card form — hide Mada/Visa/Master tabs; brand is detected from card number. */
  hideBrandSelector?: boolean;
}

export const HyperPayCopyAndPayWidget: React.FC<HyperPayCopyAndPayWidgetProps> = ({
  config,
  scriptId = "hyperpay-widget-script",
  dataBrands = "VISA MASTER MADA",
  variant = "card",
  abovePayButtonSlot,
  getSaveCardForSubmit,
  inModal = false,
  hideBrandSelector = false,
}) => {
  const skipApplePay =
    variant === "applepay" && !canUseApplePayInBrowser();

  const isRegistrationCheckout = !!config.registrationCheckout;
  const cardBrands = (dataBrands || "VISA MASTER MADA").trim();
  const cardBrandsSafe = canUseApplePayInBrowser()
    ? cardBrands
    : stripApplePayFromBrands(cardBrands);
  const formBrands =
    variant === "applepay"
      ? "APPLEPAY"
      : cardBrandsSafe.replace(/\s+/g, " ").trim();
  const isSingleCardBrand =
    variant === "card" && cardBrandsSafe.length > 0 && !/\s/.test(cardBrandsSafe);
  const { t, i18n } = useTranslation("translation");
  const lang = i18n.language?.toLowerCase().startsWith("ar") ? "ar" : "en";
  const wrapperRef = useRef(null);
  const slotHostRef = useRef(null);
  /* OPPWA's script downloads and then builds its card iframes, which takes a
     few seconds after this component mounts. Until its onReady fires, a
     placeholder of the form holds the space so the rider never looks at an
     empty card. */
  const [widgetReady, setWidgetReady] = useState(false);
  // Read via ref inside the OPPWA callback so it always sees the latest
  // handler even though the script-injection effect below only runs once
  // per checkoutId (it must not re-run on every prop change).
  const getSaveCardForSubmitRef = useRef(getSaveCardForSubmit);
  useEffect(() => {
    getSaveCardForSubmitRef.current = getSaveCardForSubmit;
  }, [getSaveCardForSubmit]);

  const relocateSaveCardSlot = useCallback(() => {
    const root = wrapperRef.current;
    const slotHost = slotHostRef.current;
    if (!root || !slotHost || !abovePayButtonSlot) return;
    mountSaveCardSlotAbovePayButton(root, slotHost);
  }, [abovePayButtonSlot]);

  useEffect(() => {
    if (!config?.checkoutId || !config?.widgetBaseUrl) return;

    unloadHyperPayWidgetScript(scriptId);
    setWidgetReady(false);

    let active = true;
    const markReady = () => {
      if (active) setWidgetReady(true);
    };
    /* Never leave the rider on the placeholder: if onReady does not arrive
       (blocked script, slow network), show whatever OPPWA has rendered. */
    const revealFallback = setTimeout(markReady, 15000);

    applyWpwlOptionsWithSanitizer(
      sanitizeHyperPayWpwlOptions(config.wpwlOptions),
      getSaveCardForSubmitRef,
      isRegistrationCheckout,
      inModal,
      hideBrandSelector,
      lang,
      markReady
    );

    const script = document.createElement("script");
    script.id = scriptId;
    const registrationSuffix = isRegistrationCheckout ? "/registration" : "";
    script.src = `${config.widgetBaseUrl.replace(/\/$/, "")}/v1/paymentWidgets.js?checkoutId=${config.checkoutId}${registrationSuffix}`;
    script.async = true;
    // SRI hash from checkout API is for payment widget URL only — skip for /registration.
    if (config.integrity && !isRegistrationCheckout) {
      script.crossOrigin = "anonymous";
      script.integrity = config.integrity;
    }
    script.onerror = markReady;
    document.body.appendChild(script);

    return () => {
      active = false;
      clearTimeout(revealFallback);
      unloadHyperPayWidgetScript(scriptId);
    };
  }, [
    config?.checkoutId,
    config?.widgetBaseUrl,
    config?.integrity,
    config?.wpwlOptions,
    isRegistrationCheckout,
    scriptId,
    inModal,
    hideBrandSelector,
    lang,
  ]);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;
    let t;
    const schedule = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        hideUnwantedWpwlGroups({ registrationCheckout: isRegistrationCheckout });
        hideHyperPayFieldLabels();
        if (canUseApplePayInBrowser()) {
          moveApplePaySectionToTop();
        }
        applyBrandUi(root, hideBrandSelector);
        if (inModal) flattenInModalCardChrome(root);
        forceFullWidthWpwlFieldGroups(root);
        relocateSaveCardSlot();
      }, 30);
    };
    const obs = new MutationObserver(schedule);
    obs.observe(root, { childList: true, subtree: true });
    schedule();
    return () => {
      obs.disconnect();
      clearTimeout(t);
    };
  }, [
    config?.checkoutId,
    scriptId,
    isRegistrationCheckout,
    inModal,
    hideBrandSelector,
    relocateSaveCardSlot,
  ]);

  useEffect(() => {
    relocateSaveCardSlot();
  }, [relocateSaveCardSlot, config?.checkoutId]);

  if (skipApplePay || !config?.return_url) return null;

  return (
    <div
      ref={wrapperRef}
      dir={lang === "ar" ? "rtl" : "ltr"}
      className={`hyperpay-widget-wrapper${widgetReady ? " is-ready" : " is-loading"}${isSingleCardBrand ? " hyperpay-widget--single-brand" : ""}${hideBrandSelector ? " hyperpay-widget--no-brand-tabs" : ""}${isRegistrationCheckout ? " hyperpay-widget--registration" : ""}${inModal ? " hyperpay-widget--in-modal" : ""}`}
    >
      {!widgetReady && (
        <div
          className={`hyperpay-skeleton${variant === "applepay" ? " is-applepay" : ""}`}
          role="status"
          aria-live="polite"
        >
          {variant === "applepay" ? (
            <span className="hyperpay-skeleton__btn" />
          ) : (
            <>
              <span className="hyperpay-skeleton__pills" />
              <span className="hyperpay-skeleton__field" />
              <span className="hyperpay-skeleton__row">
                <span className="hyperpay-skeleton__field" />
                <span className="hyperpay-skeleton__field" />
              </span>
              <span className="hyperpay-skeleton__field" />
              <span className="hyperpay-skeleton__btn" />
            </>
          )}
          <span className="hyperpay-skeleton__note">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="5" y="10.5" width="14" height="10" rx="2.5" />
              <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
            </svg>
            <bdi>{t("loadingSecurePayment", "Loading secure payment form")}</bdi>
          </span>
        </div>
      )}
      {abovePayButtonSlot ? (
        <div ref={slotHostRef} className="hyperpay-save-card-slot-host">
          {abovePayButtonSlot}
        </div>
      ) : null}
      <form action={config.return_url} data-brands={formBrands} className="paymentWidgets" />
    </div>
  );
};
